/**
 * The scanned label — `scanned: true` on everything a scan produces.
 *
 * Stamped once, at the end of post-merge processing (just before the
 * "Post-Processing Complete" checkpoint), on every object, wall, floor,
 * ceiling, door, window and opening the pipeline leaves in a room and on the
 * structure document, and on every finish region the default-finishes pass
 * painted. A reprocess rewrites the rooms and re-stamps them.
 *
 * What the label MEANS: the element is an existing condition. Nothing has
 * changed it since the scan, so it carries no material or install price. An
 * edit strips the label (and records the scanned version in the demolished
 * bucket); a replacement makes a new, unlabelled object. Plan and the model
 * behind it: claude-docs/2026-10-08-existing-conditions-pricing.md.
 *
 * What it is NOT: `scanAdjusted` (geometry the pipeline inferred rather than
 * measured — a scanned element can carry both) and `asFound` (the
 * surface-condition pass's verdict on what state the element is in).
 *
 * Pure. No I/O, no mutation of the input; the writer updates only the arrays
 * `changed` names.
 */

const SCANNED_FIELD = 'scanned';

/** The room arrays whose elements are scanned, and which carry finish regions. */
const ROOM_ARRAYS = [
  'objects',
  'walls',
  'floors',
  'ceilings',
  'doors',
  'windows',
  'openings',
];
const SURFACES_WITH_REGIONS = new Set(['walls', 'floors', 'ceilings']);

/** The structure document's own arrays, and the exterior bucket's. */
const STRUCTURE_ARRAYS = [
  'walls',
  'floors',
  'objects',
  'doors',
  'windows',
  'openings',
];
const EXTERIOR_ARRAYS = ['walls', 'doors', 'windows', 'openings'];

const isScanned = (el) => el?.[SCANNED_FIELD] === true;

const isRecord = (el) => el !== null && typeof el === 'object';

/**
 * Label one element. Returns the input itself when it is already labelled or
 * is not a record, so an unchanged array can be told from a changed one by
 * identity.
 */
function stampElement(el, counter) {
  if (!isRecord(el)) return el;
  counter.count += 1;
  return isScanned(el) ? el : { ...el, [SCANNED_FIELD]: true };
}

/** A surface with its regions labelled too; `regions` counts them. */
function stampSurface(surface, counter, regions) {
  const stamped = stampElement(surface, counter);
  const list = surface?.texture?.regions;
  if (!Array.isArray(list)) return stamped;
  let changed = false;
  const next = list.map((region) => {
    const out = stampElement(region, regions);
    if (out !== region) changed = true;
    return out;
  });
  if (!changed) return stamped;
  return {
    ...stamped,
    texture: { ...stamped.texture, regions: next },
  };
}

/** Label every element of `list`; null when nothing changed. */
function stampArray(list, stampOne) {
  if (!Array.isArray(list)) return null;
  let changed = false;
  const next = list.map((el) => {
    const out = stampOne(el);
    if (out !== el) changed = true;
    return out;
  });
  return changed ? next : null;
}

/**
 * @param {Object} room  a room document
 * @returns {{ room: Object, counts: Object<string, number>, changed: Set<string> }}
 *   `counts` is how many elements (and `regions`) carry the label after the
 *   stamp; `changed` names the arrays whose contents it altered.
 */
function stampScannedRoom(room) {
  const counts = {};
  const regions = { count: 0 };
  const changed = new Set();
  const out = { ...room };
  for (const key of ROOM_ARRAYS) {
    const counter = { count: 0 };
    const stampOne = SURFACES_WITH_REGIONS.has(key)
      ? (el) => stampSurface(el, counter, regions)
      : (el) => stampElement(el, counter);
    const next = stampArray(room?.[key], stampOne);
    if (next) {
      out[key] = next;
      changed.add(key);
    }
    counts[key] = counter.count;
  }
  counts.regions = regions.count;
  return { room: out, counts, changed };
}

/**
 * @param {Object} structure  a structure document
 * @returns {{ structure: Object, counts: Object<string, number>, changed: Set<string> }}
 *   `counts` folds the exterior bucket into the same keys; `changed` names
 *   the top-level fields altered (`exterior` as one field, since it is
 *   written as one map).
 */
function stampScannedStructure(structure) {
  const counts = {};
  const changed = new Set();
  const out = { ...structure };
  const counters = {};
  const counterFor = (key) => {
    if (!counters[key]) counters[key] = { count: 0 };
    return counters[key];
  };
  for (const key of STRUCTURE_ARRAYS) {
    const counter = counterFor(key);
    const next = stampArray(structure?.[key], (el) =>
      stampElement(el, counter)
    );
    if (next) {
      out[key] = next;
      changed.add(key);
    }
  }
  const exterior = structure?.exterior;
  if (isRecord(exterior)) {
    const nextExterior = { ...exterior };
    let exteriorChanged = false;
    for (const key of EXTERIOR_ARRAYS) {
      const counter = counterFor(key);
      const next = stampArray(exterior[key], (el) => stampElement(el, counter));
      if (next) {
        nextExterior[key] = next;
        exteriorChanged = true;
      }
    }
    if (exteriorChanged) {
      out.exterior = nextExterior;
      changed.add('exterior');
    }
  }
  for (const key of STRUCTURE_ARRAYS) counts[key] = counterFor(key).count;
  return { structure: out, counts, changed };
}

module.exports = {
  SCANNED_FIELD,
  ROOM_ARRAYS,
  STRUCTURE_ARRAYS,
  EXTERIOR_ARRAYS,
  isScanned,
  stampScannedRoom,
  stampScannedStructure,
};
